import { forwardRef, memo } from 'react'
import { useAppSelector } from 'src/state/hooks'
import AdornedTable from './AdornedTable/AdornedTable'
import { TableLoading, Error, TableNoData } from '../NotAvailable'
import Data, { isNull, isEmpty, selectRecords, selected } from '../Data/Data'
export { selectRecords, isNull, isEmpty, urlParam } from '../Data/DataUtils'

/**
 * @typedef {Object} ListProps To be called by EvictionList, ForeclosureList, etc
 * @prop {string} [title] The title displayed above the table
 * @prop {ReactNode} [subTitle] Additional text displayed beneath the title, if any
 * @prop {string} [noDataMessage] Text to display if there is no data
 * @prop {AvailableSlices} slice Slice with the full list of evictions
 * @prop {import('./AdornedTable/AdornedTable').AboutData} aboutData
 * Object with info to display under "about" section
 * @prop {string} csvFileName
 * @prop {(rows: any[]) => void} [onSearchFilter] Callback when rows are filtered
 * @prop {() => ReactNode} [searchHint] Content to display when rows are filtered
 * @prop {number} [paginationPageSize]
 * @prop {boolean} [hideUnpopulatedColumns]
 */

/** I believe extra CSSProperties do not get called in TableHeader
 * @typedef {{
 *  field: string,
 *  headerName?: string,
 *  type?: string,
 *  renderCell?: (p: any) => ReactNode,
 *  valueFormatter?: (p: any) => string,
 *  searchSelector?: (p: any) => string
 *  sortSelector?: (p: any) => any
 *  numeric?: boolean
 *  disablePadding?: boolean
 * } & CSSProperties} Column 
 */

/** 
 * @typedef {Object} DataTablePropsBase
 * @prop {any[]} [fetchArray] 
 * @prop {any[]} rows
 * @prop {Column[]} columns
 * @prop {string} [title]
 * @prop {boolean} [isDebugEnabled]
 * @prop {any} [select]
 * @prop {string} [keyField]
 * @prop {string} [className]
 * 
 * @typedef {DataTablePropsBase & import("./AdornedTable/AdornedTable").AdornedTableProps} DataTableProps
 * */

/** DataTable: an optionally data-enabled version of the StyledTable component
 * @param {DataTableProps} props
 * @param {Ref<HTMLDivElement>} ref
 * Note: either the fetch or rows prop must be provided
*/
const DataTable = (
  {
    fetchArray,
    rows = [],
    columns = [],
    isDebugEnabled = false,
    title,
    select = {},
    keyField = null,
    className,
    ...rest
  },
  ref
) => {
  const firstFetchItem = fetchArray?.[0] ?? { slice: null, url: null, select: null }
  const { slice, url } = firstFetchItem

  /* Set hooks to use Redux global state */
  const json = useAppSelector(selectRecords(slice))

  /* Wait for a source of data to become available */
  if (isNull(fetchArray) && isEmpty(rows)) {
    return null
  }

  const rowSelect = select.row
  const columnSelect = select.column
  let passDownRows = []
  let passDownColumns = []
  let passDownFetch = null

  /* Option 1: this component accepts rows and columns props and passes them to StyledTable */
  /* Option 2: this component accepts a fetch prop and passes fetch to Data and then the data to StyledTable when available */
  if (!isEmpty(rows) && !isEmpty(columns)) {
    if (isDebugEnabled)
      console.info(
        '%c DataTable: populating the table with data and columns props',
        'color: green'
      )
    passDownRows = rows
    passDownColumns = columns
    passDownFetch = null
  } else {
    if (isNull(slice) || isNull(url)) {
      console.error('%c DataTable: either the rows property or fetch property must be valid')
    }

    const isPreFetch = isEmpty(json)
    if (isPreFetch) {
      if (isDebugEnabled)
        console.info(`%c Fetching slice '${slice}' from '${url}'`, 'color: green')
    } else {
      if (isDebugEnabled)
        console.info('%c Populating the table with fetched data', 'color: green')

      /* The data can be either an object or array in JSON */
      let rowDataJSON = selected(json, rowSelect)

      /* Determine row data to pass down to the grid component */
      const rowDataAsArray = Object.assign([], rowDataJSON)
      passDownRows = rowDataAsArray

      /* Determine column data to pass down to the grid component */
      /* TODO: data-driven/auto-generated columns needs a bit more work and isn't used at this time */
      if (isEmpty(columns)) {
        /* Data-Driven Columns */
        const columnDataJSON = selected(json, columnSelect)
        if (!isEmpty(columnDataJSON)) {
          if (isDebugEnabled)
            console.info(
              '%c DataTable: using data-driven column definitions from JSON column data',
              'color: green'
            )
          passDownColumns = columnDataJSON
        } else {
          if (isDebugEnabled)
            console.info(
              '%c DataTable: using auto-generated column definitions extracted from JSON row data',
              'color: green'
            )
          const columns = Object.keys(rowDataAsArray[0]).map(x => ({ field: x, headerName: x }))
          const filteredColumns = !isEmpty(columns)
            ? columns.filter(x => columns.includes(x.field))
            : columns
          passDownColumns = filteredColumns
        }
      } else {
        /* Manually-Defined Columns */
        if (isDebugEnabled)
          console.info(
            '%c DataTable: using manually-defined column definitions from props',
            'color: green'
          )
        passDownColumns = columns
      }
    }

    passDownFetch = [firstFetchItem]
  }

  return (
    <Data {...rest} fetchArray={passDownFetch} isDebugEnabled={isDebugEnabled}>
      <Data.Pending component={TableLoading} />
      <Data.Rejected component={Error} />
      <Data.NoData>
        <TableNoData {...rest} />
      </Data.NoData>
      <Data.Timeout component={TableNoData} secondsToWait={10} />
      <Data.Fulfilled>
        <AdornedTable
          {...rest}
          className={className}
          columns={passDownColumns}
          rows={passDownRows}
          title={title}
          ref={ref}
          isDebugEnabled={isDebugEnabled}
        />
      </Data.Fulfilled>
    </Data>
  )
}

export default memo(forwardRef(DataTable))
